import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

const AIAssistantContext = createContext(null);

let scriptInjected = false;

const injectConvocoreScript = () => {
  if (typeof window === "undefined") return;
  if (scriptInjected) return;
  scriptInjected = true;

  window.VG_CONFIG = {
    ID: "1HqorpKKnCVxWI3GUyN3",
    region: "na",
    render: "full-width",
    stylesheets: ["https://cdn.convocore.ai/vg_live_build/styles.css"],
  };
  const s = document.createElement("script");
  s.src = "https://cdn.convocore.ai/vg_live_build/vg_bundle.js";
  s.defer = true;
  document.body.appendChild(s);
};

export const AIAssistantProvider = ({ children }) => {
  const [open, setOpen] = useState(false);

  // Inject Convocore script once on mount so the widget is warm before the
  // user opens the modal. The widget renders into #VG_OVERLAY_CONTAINER
  // which lives inside the modal panel (always in the DOM).
  useEffect(() => {
    const t = setTimeout(injectConvocoreScript, 800);
    return () => clearTimeout(t);
  }, []);

  const openAssistant = useCallback(() => {
    injectConvocoreScript();
    setOpen(true);
  }, []);

  const closeAssistant = useCallback(() => setOpen(false), []);

  useEffect(() => {
    if (typeof document === "undefined") return;
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const value = useMemo(
    () => ({ open, openAssistant, closeAssistant }),
    [open, openAssistant, closeAssistant],
  );

  return (
    <AIAssistantContext.Provider value={value}>
      {children}
    </AIAssistantContext.Provider>
  );
};

export const useAIAssistant = () => {
  const ctx = useContext(AIAssistantContext);
  if (!ctx) throw new Error("useAIAssistant must be used inside AIAssistantProvider");
  return ctx;
};
